import React, { useState } from 'react';
import { CalendarLayoutModelRowOutDTO } from '@api/ringil-api.v2';
import Tabs from '@components/ringil3/RingilTabs/Tabs.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation';
import { useCalendarLayoutConfig } from '../context';
import { CalendarLayoutModelsTable } from './CalendarLayoutModelsTable';
import { CalendarLayoutAssignmentsTable } from './CalendarLayoutAssignmentsTable';

interface CalendarLayoutManagementProps {
  /** Page title. Owned here so the create action can sit on the heading row, as other lists do. */
  title: string;
  /**
   * Admin mode exposes system-wide authoring, the system default, and every company's assignments.
   * Company mode shows the same screens narrowed to the caller's own company — the backend enforces
   * the narrowing, this only decides what is worth rendering.
   */
  admin: boolean;
}

type Tab = 'models' | 'assignments';

/**
 * The calendar layout management screen: models, where they are applied, and the designer.
 *
 * One component for both the admin tool and the customer settings page, because the difference
 * between them is a handful of affordances rather than a different workflow.
 */
export const CalendarLayoutManagement: React.FC<CalendarLayoutManagementProps> = ({ admin, title }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const { reload: reloadRenderedLayouts } = useCalendarLayoutConfig();

  const [tab, setTab] = useState<Tab>('models');
  // Bumped to force the tables to refetch after a change made on the other tab, so "applied to"
  // counts and rendered models never drift from what was just done.
  const [refreshToken, setRefreshToken] = useState(0);
  // Bumped to ask the models table to open its create dialog; the button lives up here.
  const [createSignal, setCreateSignal] = useState(0);

  const refresh = () => {
    setRefreshToken(token => token + 1);
    // The calendar shares the provider with this screen, so a change here is visible there without
    // a reload.
    reloadRenderedLayouts();
  };

  // Editing a model is its own page, so it gets a URL, a breadcrumb and the browser's back button
  // rather than a hidden mode of this screen.
  // One place decides where a model lives, used both for navigating and for the name link.
  const modelUrl = (row: CalendarLayoutModelRowOutDTO) =>
    admin ? navigation.urlFunctions.adminCalendarLayoutModel(row.id) : navigation.urlFunctions.calendarLayoutModel(row.id);

  const openModel = (row: CalendarLayoutModelRowOutDTO) => navigation.navigate(modelUrl(row));
  const modelHref = (row: CalendarLayoutModelRowOutDTO) => navigation.createNavigationLink(modelUrl(row));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center">
        <Heading title={title} headingVariant={'h1'} />
        {/* Same header on both tabs. From the assignments tab it switches to the models tab first,
            since that is where the create dialog lives — and where the new model will appear. */}
        <RingilButton
          mode="link"
          label={tr('CalendarLayout.createModel', 'New model')}
          icon={<i className="pi pi-plus" />}
          onClick={() => {
            setTab('models');
            setCreateSignal(signal => signal + 1);
          }}
        />
      </div>

      <Tabs
        tabs={[
          { label: tr('CalendarLayout.tab.models', 'Models'), active: tab === 'models' },
          { label: tr('CalendarLayout.tab.whereApply', 'Where they apply'), active: tab === 'assignments' },
        ]}
        onTabChange={index => setTab(index === 0 ? 'models' : 'assignments')}
      />

      {tab === 'models' ? (
        <CalendarLayoutModelsTable
          admin={admin}
          onEdit={openModel}
          modelHref={modelHref}
          refreshToken={refreshToken}
          createSignal={createSignal}
          onChanged={refresh}
        />
      ) : (
        <CalendarLayoutAssignmentsTable admin={admin} refreshToken={refreshToken} onChanged={refresh} />
      )}
    </div>
  );
};
